/**
 * Embeddable export button (F3-J): downloads the entity as xlsx/csv with the
 * hosting screen's filter context. Example:
 *
 * ```tsx
 * <ExportButton entityKey="io.students" orgId={activeOrgId()} filters={{ estado: "active" }} />
 * ```
 */

import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Download, Loader2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { buildExportPath, buildWorkbookPath, downloadIoFile } from "../api";

interface ExportButtonProps {
  /** Entidad a exportar (obligatoria salvo en modo workbook). */
  entityKey?: string;
  /** Multi-sheet workbook mode: the workbook's key (e.g. io.workbook.cohort). */
  workbookKey?: string;
  /** Active organization (required for org-scoped entities). */
  orgId?: string;
  /** The screen's current filters (passed as query params). */
  filters?: Record<string, string | undefined>;
  /** Offered formats; defaults to xlsx + csv (the workbook is xlsx-only). */
  formats?: ("xlsx" | "csv")[];
  size?: "sm" | "default";
  class?: string;
}

export function ExportButton(props: ExportButtonProps) {
  const { t } = useI18n();
  const [busy, setBusy] = createSignal<"xlsx" | "csv" | null>(null);

  const download = async (format: "xlsx" | "csv") => {
    setBusy(format);
    try {
      if (props.workbookKey) {
        const path = buildWorkbookPath(props.workbookKey, props.orgId, props.filters ?? {});
        const stem = props.workbookKey.replace(/^io\.workbook\./, "");
        await downloadIoFile(path, `${stem}.xlsx`);
      } else {
        const key = props.entityKey ?? "";
        const path = buildExportPath(key, format, props.orgId, props.filters ?? {});
        const stem = key.replace(/^io\./, "");
        await downloadIoFile(path, `${stem}.${format}`);
      }
    } catch (e) {
      showToast(`${t(msg`Export failed`)}: ${e instanceof Error ? e.message : String(e)}`, "error");
    } finally {
      setBusy(null);
    }
  };

  const formats = () =>
    props.workbookKey ? (["xlsx"] as const) : (props.formats ?? (["xlsx", "csv"] as const));
  const testKey = () => props.workbookKey ?? props.entityKey ?? "io";

  return (
    <div class={`inline-flex items-center gap-1.5 ${props.class ?? ""}`}>
      <For each={formats()}>
        {(format) => (
          <Button
            variant="outline"
            size={props.size ?? "sm"}
            disabled={busy() !== null}
            onClick={() => void download(format)}
            data-testid={`io-export-${testKey()}-${format}`}
          >
            <Show when={busy() === format} fallback={<Download class="h-4 w-4" />}>
              <Loader2 class="h-4 w-4 animate-spin" />
            </Show>
            <span class="ms-1.5 uppercase">{format}</span>
          </Button>
        )}
      </For>
    </div>
  );
}
